<template>
  <div>
    <b-breadcrumb size="is-large" separator="has-succeeds-separator" aria-label="breadcrumbs">
        <b-breadcrumb-item tag="a" @click.prevent="$router.push({name: 'device-list'})">
          {{ $t('global.devices') }}
        </b-breadcrumb-item>
        <b-breadcrumb-item active>
            {{ $t('firmwares.title') }}
        </b-breadcrumb-item>
    </b-breadcrumb>
    <div class="list-table-container">
      <b-table :data="rows" striped :loading="loading" @click="handleRowClick"
        sort-icon="menu-up" default-sort="createdAt" default-sort-direction="desc"
        group-field="projectId" :group-sort="[{ field: 'projectId', order: 'asc' }]"
        :group-options="{ collapsible: true, defaultOpen: true, rowHeader: false }">
        <b-table-column field="sha256" sortable :label="$t('firmwares.checksum')" v-slot="props">
          <div class="is-flex is-align-items-center is-family-monospace">
            <div class="sha-cell">{{ props.row.sha256 }}</div>
          </div>
        </b-table-column>
        <b-table-column field="projectName" sortable :label="$t('projects.title').slice(0,-1)" v-slot="props">
          <template v-if="props.row.projectId && (props.row.projectName || props.row.projectId)">
            <router-link
              :to="{ name: 'project-edit', params: { id: props.row.projectId } }"
              @click.stop
            >
              {{ props.row.projectName || String(props.row.projectId) }}
            </router-link>
          </template>
          <template v-else>
            {{ props.row.projectName || '—' }}
          </template>
        </b-table-column>
        <b-table-column field="productId" sortable :label="$t('firmwares.product')" v-slot="props">
          {{ props.row.productId || '—' }}
        </b-table-column>
        <b-table-column field="size" sortable label="Size" v-slot="props">
          {{ formatSize(props.row.size) }}
        </b-table-column>
        <b-table-column field="createdAt" sortable :label="$t('firmwares.created')" v-slot="props">
          {{ $d(new Date(props.row.createdAt), 'short') }}
        </b-table-column>
        <b-table-column field="accessedAt" sortable :label="$t('firmwares.lastAccessed')" v-slot="props">
          {{ props.row.accessedAt ? $d(new Date(props.row.accessedAt), 'short') : '—' }}
        </b-table-column>
        <b-table-column field="downloadCount" sortable :label="$t('firmwares.downloads')" v-slot="props">
          {{ props.row.downloadCount ?? 0 }}
        </b-table-column>
        <b-table-column label="Actions" v-slot="props">
          <div class="control is-flex is-align-items-center">
            <b-button type="is-primary" size="is-small" icon-left="download" :title="$t('global.download')" aria-label="download" class="mr-2" @click.stop="downloadFirmware(props.row)" />
            <b-button type="is-danger" size="is-small" icon-left="delete" :title="$t('global.delete')" aria-label="delete" @click.stop="confirmRemove(props.row)" />
          </div>
        </b-table-column>
        <template #empty>
          <div class="has-text-centered mt-4 mb-4">{{ $t('firmwares.noneYet') }}</div>
        </template>
      </b-table>
    </div>
  </div>
  </template>

<script lang="ts">
import { defineComponent, onMounted, computed, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import KeyCloakService from '@/security/KeycloakService'
import { formatSize } from '@/utils/formatters'

type FirmwareMeta = {
  sha256: string
  productId?: string
  createdAt?: string
  updatedAt?: string
  accessedAt?: string
  downloadCount?: number
    size?: number
}

export default defineComponent({
  name: 'FirmwareList',
  setup() {
    const router = useRouter()
    const store = useStore()
    const rows = computed<FirmwareMeta[]>(() => {
      const data = (store.state as any).firmwares
      if (Array.isArray(data)) return data as FirmwareMeta[]
      return []
    })
    const loading = computed<boolean>(() => store.state.loading.firmwares)
    const vm = getCurrentInstance()

    const load = async (force = false) => {
      await store.dispatch('loadModel', { model: 'firmwares', force })
    }

    const remove = async (row: FirmwareMeta) => {
      try {
        await store.dispatch('deleteFirmware', { sha: row.sha256 })
      } catch (e) {
        // ignore
      }
    }

    const confirmRemove = (row: FirmwareMeta) => {
      type DialogProxy = { $buefy?: { dialog?: { confirm?: (opts: { message: string; type?: string; confirmText?: string; cancelText?: string; hasIcon?: boolean; onConfirm?: () => void }) => void } } }
      const proxy = vm?.proxy as unknown as DialogProxy & { $t?: (key: string, vars?: Record<string, string>) => string }
      const msg = proxy?.$t ? proxy.$t('firmwares.confirmDelete', { sha: row.sha256 }) : `Delete firmware ${row.sha256}?`
      const confirmText = proxy?.$t ? proxy.$t('global.delete') : 'Delete'
      const cancelText = proxy?.$t ? proxy.$t('global.cancel') : 'Cancel'
      proxy?.$buefy?.dialog?.confirm?.({
        message: msg,
        type: 'is-danger',
        confirmText,
        cancelText,
        hasIcon: true,
        onConfirm: () => { remove(row) }
      })
    }

    // use shared formatter from utils

    const shortSha = (sha?: string) => {
      if (!sha) return ''
      // show first 8 and last 6 characters with ellipsis in between
      if (sha.length <= 16) return sha
      return `${sha.slice(0, 8)}…${sha.slice(-6)}`
    }

    const downloadFirmware = async (row: FirmwareMeta) => {
      try {
        const resp = await fetch(`/api/v1/firmwares/${encodeURIComponent(row.sha256)}`, {
          headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` }
        })
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const blob = await resp.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `firmware-${row.sha256}.bin`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        // swallow
      }
    }


    const handleRowClick = (row: FirmwareMeta) => {
      router.push({ name: 'firmware-details', params: { sha: row.sha256 } })
    }

    onMounted(load)
    return { rows, loading, confirmRemove, shortSha, formatSize, downloadFirmware, handleRowClick }
  }
})
</script>

<style scoped>
.list-table-container {
  margin-top: 1rem;
}
.sha-cell {
  display: inline-block;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>


